<template>
  <div v-if='$store.getters["user/hasDSSTitrationAccess"]' v-disable-all="hasReadOnlyAccess">
    <v-row no-gutters>
      <v-col>
        <div class="dss__subheader-main mb-6 mt-5">
          <div>
            <svg style="width:16px;height:16px" class="mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M6,22A3,3 0 0,1 3,19C3,18.4 3.18,17.84 3.5,17.37L9,7.81V6A1,1 0 0,1 8,5V4A2,2 0 0,1 10,2H14A2,2 0 0,1 16,4V5A1,1 0 0,1 15,6V7.81L20.5,17.37C20.82,17.84 21,18.4 21,19A3,3 0 0,1 18,22H6M5,19A1,1 0 0,0 6,20H18A1,1 0 0,0 19,19C19,18.79 18.93,18.59 18.82,18.43L16.53,14.47L14,17L8.93,11.93L5.18,18.43C5.07,18.59 5,18.79 5,19M13,10A1,1 0 0,0 12,11A1,1 0 0,0 13,12A1,1 0 0,0 14,11A1,1 0 0,0 13,10Z" />
            </svg>
          </div>
          <h3 class="pb-1">{{ $ucfirst($t("dss.titrationSchemes")) }}</h3>
        </div>
        <v-row no-gutters>
          <v-col v-if="titrationSchemes && titrationSchemes.length">
            <span v-for="(scheme) in titrationSchemes" v-bind:key="scheme.id">
              <span :style="{ display: softDeletedId.includes(scheme.id) ? 'none' : 'block' }">
                <dosage-titration-scheme-card
                    :ref="'titrations'"
                    :titrationScheme="scheme"
                    v-if="scheme.type === 'DOSAGE'"
                    v-on:save-scheme="saveTitrationScheme"
                    v-on:delete-scheme="deleteTitrationScheme"
                />
                <drug-addition-titration-scheme-card
                    :ref="'titrations'"
                    :titrationScheme="scheme"
                    :medicationClassesToChooseFrom="medicationClasses"
                    v-if="scheme.type === 'DRUG_ADDITION'"
                    v-on:save-scheme="saveTitrationScheme"
                    v-on:delete-scheme="deleteTitrationScheme"
                />
              </span>
            </span>
          </v-col>
          <v-col
            cols="12"
            v-if="(titrationSchemes === null
              || titrationSchemes.length === 0
              || softDeletedId.length === titrationSchemes.length)
              && !showAddDosageTitrationScheme
              && !showAddDrugAdditionTitrationScheme"
          >
            <p class="ml-6">{{ $ucfirst($t("dss.noTitrationSchemesRegistered")) }}</p>
          </v-col>
          <v-col v-if="showAddDosageTitrationScheme" cols="12">
            <add-dosage-titration-scheme-card
                ref="add-dosage-titration-scheme-card"
                v-on:save-scheme="saveTitrationScheme"
                v-on:cancel-adding-scheme="cancelAddingDosageTitrationScheme"
            />
          </v-col>
          <v-col v-if="showAddDrugAdditionTitrationScheme" cols="12">
            <add-drug-addition-titration-scheme-card
                ref="add-drug-addition-titration-scheme-card"
                v-on:save-scheme="saveTitrationScheme"
                v-on:cancel-adding-scheme="cancelAddingDrugAdditionTitrationScheme"
                :medicationClassesToChooseFrom="medicationClasses"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="right">
            <v-btn
              class="mr-3"
              x-large
              text
              @click="showAddDosageTitrationScheme = true"
            >
              <eva-icon class="mr-2 medication__icon" name="plus-circle-outline"/>
              {{ $ucfirst($t("dss.addDosageTitrationScheme")) }}
            </v-btn>
            <v-btn
              x-large
              text
              @click="showAddDrugAdditionTitrationScheme = true"
            >
              <eva-icon class="mr-2 medication__icon" name="plus-circle-outline"/>
              {{ $ucfirst($t("dss.addDrugAdditionTitrationScheme")) }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <unsaved-changes-warning
        ref="unsavedChangesWarning"
        screen="titrationSchemes"
        :custom-leave-message="$t('medication.leaveScreen')"
    />
  </div>
</template>

<script>
import DosageTitrationSchemeCard from "../../components/Parts/DSS/DosageTitrationSchemeCard.vue";
import DrugAdditionTitrationSchemeCard from "../../components/Parts/DSS/DrugAdditionTitrationSchemeCard.vue";
import AddDosageTitrationSchemeCard from "../../components/Parts/DSS/AddDosageTitrationSchemeCard.vue";
import AddDrugAdditionTitrationSchemeCard from "../../components/Parts/DSS/AddDrugAdditionTitrationSchemeCard.vue";
import UnsavedChangesWarning from "../../components/Parts/UnsavedChangesWarning.vue";
import gql from "graphql-tag";
import {mapGetters} from "vuex";

/**
 * Component DSSTitrationSchemes.vue
 *
 * @module DSSTitrationSchemes
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/views/DSS/DSSTitrationSchemes.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "DSSTitrationSchemes",
  components: {
    DosageTitrationSchemeCard,
    DrugAdditionTitrationSchemeCard,
    AddDosageTitrationSchemeCard,
    AddDrugAdditionTitrationSchemeCard,
    UnsavedChangesWarning
  },
  created() {
    this.$app.eventBus.$on('DSSHeader.saveAndExit', this.saveAndExit)
  },
  beforeDestroy(){
    this.$app.eventBus.$off('DSSHeader.saveAndExit', this.saveAndExit)
    window.removeEventListener('beforeunload', this.deleteAllQueuedTitrationSchemes);
  },
  data: function () {
    return {
      titrationSchemes: null,
      medicationClasses: [],
      currentlyEditing: null,
      showAddDosageTitrationScheme: false,
      showAddDrugAdditionTitrationScheme: false,
      softDeletedId: [],
      timeOutDelete: {},
    };
  },
  computed: {
    ...mapGetters({
      patientId: "patientSummary/id",
      unsavedChanges: 'patientSummary/unsavedChanges',
      hasReadOnlyAccess: 'user/hasReadOnlyAccess'
    }),
  },
  watch: {
    patientId: function () {
      this.getTitrationSchemes();
    },
  },
  mounted: function () {
    this.getTitrationSchemes();
    this.getMedicationClasses();
    // before refreshing or leaving screen (app) completely
    // delete all queued titration schemes
    window.addEventListener('beforeunload', this.deleteAllQueuedTitrationSchemes)
  },
  methods: {
    saveAndExit() {
      let cannotLeave = false;
      const refs = this.$refs['titrations']

      const titrationComponents = refs ? Array.from(refs) : [];

      if (this.$refs['add-dosage-titration-scheme-card']) {
        titrationComponents.push(this.$refs['add-dosage-titration-scheme-card'])
      }

      if (this.$refs['add-drug-addition-titration-scheme-card']) {
        titrationComponents.push(this.$refs['add-drug-addition-titration-scheme-card'])
      }

      titrationComponents.forEach((component) => {
        if (component.editMode && !component.valid) {
          cannotLeave = true;
        }
      })

      if (!cannotLeave) {
        titrationComponents.forEach((component) => {
          if (component.editMode) {
            this.saveTitrationScheme(component.tempData)
          }
        })

        this.$router.push({name: 'DSSMedicationPrescription'})
      }
    },
    getTitrationSchemes() {
      this.$app.apolloClient
          .query({
            query: gql`
            query getTitrationSchemes($patientId: String!) {
              patientTitrationSchemes(
                functions: [{ function: patientId, parameters: [$patientId] }]
                orders: [{ field: createdAt, direction: asc }]
              ) {
                id
                type
                name
                description
                startClasses {
                  key
                  name
                }
                additionalClasses {
                  key
                  name
                }
                startDosage {
                  doseAndRate {
                    id
                    sequence
                    doseQuantity {
                      code
                      value
                    }
                  }
                }
                endDosage {
                  doseAndRate {
                    id
                    sequence
                    doseQuantity {
                      code
                      value
                    }
                  }
                }
              }
            }
          `,
            variables: {
              patientId: this.patientId,
            },
          })
          .then((result) => {
            const res = result.data.patientTitrationSchemes;
            const resultIds = res.map(i => i.id);
            const stillDeleting = this.softDeletedId.filter(id => resultIds.includes(id))
            this.softDeletedId = stillDeleting
            if (!stillDeleting.length) {
              this.timeOutDelete = {};
            }
            this.titrationSchemes = res;
          });
    },
    getMedicationClasses() {
      this.$app.apolloMobileClient
          .query({
            query: gql`
            {
              medicationClasses {
                key
                name
                description
              }
            }
          `,
          })
          .then((result) => {
            this.medicationClasses = result.data.medicationClasses;
          });
    },
    medicationClassesToString(medicationClassesArray) {
      const arrayToJoin = [];

      medicationClassesArray.forEach((medicationClass) => {
        arrayToJoin.push(medicationClass.name);
      });

      return arrayToJoin.join(", ");
    },
    deleteTitrationScheme(id) {
      let found = this.titrationSchemes.find((el) => el.id === id);
      let nameOfScheme = found.name;

      this.softDeletedId = [...this.softDeletedId, id]
      const isUnsaved = this.isUnsaved(`addTitrationScheme-${id}`);

      // 'Undo' toast
      this.$toasted.show(
        `${this.$ucfirst(
          this.$t("dss.titrationSchemeHasBeenDeleted", {
            params: {
              titrationSchemeName: nameOfScheme,
            },
          })
        )}
        ${ this.$ucfirst(
          this.$t("dss.undoDeletionOftitrationScheme", {
            params: {
              titrationSchemeName: nameOfScheme,
            },
          })
        )}?`,
        {
          icon: 'undo',
          duration: 10000,
          action: {
            text: this.$ucfirst(
                this.$t("dss.undo")
            ),
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
              this.clearTimeoutDelete(id)
              if (isUnsaved) {
                this.toggleUnsaved(`addTitrationScheme-${id}`, true)
              }
            }
          },
        }
      )

      const timeout = setTimeout(() => {
        this.$app.apolloClient
            .mutate({
              mutation: gql`
              mutation deleteTitrationScheme($id: String!) {
                deleteTitrationScheme(id: $id)
              }
            `,
              variables: {
                id,
              },
            })
            .then(() => {
              this.getTitrationSchemes();
            });
      }, 10000);

      this.timeOutDelete = {...this.timeOutDelete, [id]: timeout}
      this.toggleUnsaved(`addTitrationScheme-${id}`, false)
    },

    async deleteAllQueuedTitrationSchemes() {
      await this.$store.dispatch('dss/deleteMultipleTitrationSchemes', this.softDeletedId);

      this.clearAllTimeouts();
      this.$toasted.clear();
    },

    clearTimeoutDelete(id) {
      clearTimeout(this.timeOutDelete[id]);
      this.timeOutDelete[id] = null

      const index = this.softDeletedId.indexOf(id)
      if (index >= 0) {
        const _softDeletedId = [...this.softDeletedId];
        _softDeletedId.splice(index, 1)
        this.softDeletedId = _softDeletedId;
      }
    },

    clearAllTimeouts() {
      this.softDeletedId.forEach(id => {
        clearTimeout(this.timeOutDelete[id]);
        this.toggleUnsaved(`addTitrationScheme-${id}`, false);
      })

      this.timeOutDelete = {};
      this.softDeletedId = [];
    },

    saveTitrationScheme(scheme, isNew) {
      if (scheme.type === "DOSAGE") {
        // DOSAGE.
        this.$app.apolloClient
            .mutate({
              mutation: gql`
              mutation createUpdateTitrationScheme(
                $patientId: Int
                $id: String
                $description: String
                $name: String
                $startDosageUnit: String
                $startDosageValue: Float
                $endDosageUnit: String
                $endDosageValue: Float
              ) {
                createUpdateTitrationScheme(
                  id: $id
                  patientId: $patientId
                  type: "DOSAGE"
                  description: $description
                  name: $name
                  startDosageUnit: $startDosageUnit
                  startDosageValue: $startDosageValue
                  endDosageUnit: $endDosageUnit
                  endDosageValue: $endDosageValue
                ) {
                  id
                  type
                  name
                  description
                }
              }
            `,
              variables: {
                id: scheme.id,
                patientId: parseInt(this.patientId),
                description: scheme.description || "",
                name: scheme.name,
                startDosageUnit: scheme.startDosage.code,
                startDosageValue: parseFloat(scheme.startDosage.value),
                endDosageUnit: scheme.endDosage.code,
                endDosageValue: parseFloat(scheme.endDosage.value),
              },
            })
            .then((result) => {
              if (isNew) {
                this.showAddDosageTitrationScheme = false;
              }
              this.getTitrationSchemes();
              this.toggleUnsaved(isNew ? 'addTitrationScheme-newDosage' : `addTitrationScheme-${scheme.id}`, false)
            });
      } else {
        // DRUG_ADDITION.

        let startClasses = [];
        let additionalClasses = [];
        scheme.startMedicationClasses.forEach((c) => {
          if (typeof c === "string") {
            const found = this.medicationClasses.find((e) => e.name === c);
            startClasses.push(found.key);
          } else {
            startClasses.push(c.key);
          }
        });

        scheme.additionalMedicationClasses.forEach((c) => {
          if (typeof c === "string") {
            const found = this.medicationClasses.find((e) => e.name === c);
            additionalClasses.push(found.key);
          } else {
            additionalClasses.push(c.key);
          }
        });

        this.$app.apolloClient
            .mutate({
              mutation: gql`
              mutation createUpdateTitrationScheme(
                $patientId: Int
                $id: String
                $description: String
                $name: String
                $startClasses: [String!]
                $additionalClasses: [String!]
              ) {
                createUpdateTitrationScheme(
                  id: $id
                  patientId: $patientId
                  type: "DRUG_ADDITION"
                  description: $description
                  name: $name
                  startClasses: $startClasses
                  additionalClasses: $additionalClasses
                ) {
                  id
                  type
                  name
                  description
                }
              }
            `,
              variables: {
                id: scheme.id,
                patientId: parseInt(this.patientId),
                description: scheme.description || "",
                name: scheme.name,
                startClasses: startClasses,
                additionalClasses: additionalClasses,
              },
            })
            .then((result) => {
              if (isNew) {
                this.showAddDrugAdditionTitrationScheme = false;
              }
              this.getTitrationSchemes();
              this.toggleUnsaved(isNew ? 'addTitrationScheme-newDrug' : `addTitrationScheme-${scheme.id}`, false)
            })
            .catch((error) => {
              console.warn(error);
            });
      }
    },
    cancelAddingDrugAdditionTitrationScheme() {
      this.showAddDrugAdditionTitrationScheme = false;
      this.toggleUnsaved('addTitrationScheme-newDrug', false)
    },
    cancelAddingDosageTitrationScheme() {
      this.showAddDosageTitrationScheme = false
      this.toggleUnsaved('addTitrationScheme-newDosage', false)
    },
    toggleUnsaved(name, unsaved) {
      const data = {
        unsavedChanges: unsaved,
        screen: 'titrationSchemes',
        name: name
      }
      this.$store.dispatch('patientSummary/toggleMultipleUnsavedChanges', data);
    },
    isUnsaved(name) {
      const allUnsaved = this.unsavedChanges('titrationSchemes', true);
      const unsaved = allUnsaved.find(item => item.name === name)
      return !!unsaved
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.unsavedChanges('titrationSchemes')) {
      this.$refs.unsavedChangesWarning.prepareToExit(to.name)
      return false;
    }
    // delete all queued titration schemes before leaving route
    if (this.softDeletedId.length) {
      await this.deleteAllQueuedTitrationSchemes();
    }
    next();
  }
};
</script>

<style lang="scss">
</style>
